/**
 * Honorarios / Cobranza — shell de pestañas.
 * El cobro operativo y fiscal vive en Control de cobranza (sin pestaña duplicada de listado CFDI).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { DespachoPageShell } from "@/components/despacho/DespachoUi";
import {
  honorariosApi,
  parseHonorariosError,
} from "@/modules/despacho/despachoHonorariosApi";
import {
  DESPACHO_READ_ONLY_BADGE,
  isDespachoReadOnly,
} from "@/modules/despacho/despachoPermissions";
import HonorariosMonthlyBoard from "@/pages/despacho/HonorariosMonthlyBoard";
import HonorariosOtrosCargosPanel from "@/pages/despacho/HonorariosOtrosCargosPanel";
import HonorariosClientRosterPanel from "@/pages/despacho/HonorariosClientRosterPanel";
import HonorariosOtrosCobrosPanel from "@/pages/despacho/HonorariosOtrosCobrosPanel";
import HonorariosHistorialPanel from "@/pages/despacho/HonorariosHistorialPanel";
import HonorariosContratoServiciosPanel from "@/pages/despacho/HonorariosContratoServiciosPanel";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/useCapabilities";
import { userCanAccessDespachoHonorarios } from "@/lib/teamRolePermissions";
import {
  joinHonorariosPeriod,
  readStoredHonorariosWorkPeriod,
  splitHonorariosPeriod,
  writeStoredHonorariosWorkPeriod,
} from "@/lib/honorariosWorkPeriod";

const TAB_LISTA_CLIENTES = "lista_clientes";
const TAB_CONTROL = "control";
/** Legacy URL/query: valor "facturas" redirige a Control. */
const TAB_FACTURAS_LEGACY = "facturas";
const TAB_OTROS_COBROS = "otros_cobros";
const TAB_OTROS_CARGOS = "otros_cargos";
const TAB_CONTRATO = "contrato_servicios";
const TAB_HISTORIAL = "historial";

const DEFAULT_SYNC_DATE_FROM = `${new Date().getFullYear()}-01-01`;

function normalizeHonorariosTab(value) {
  if (value === TAB_FACTURAS_LEGACY) return TAB_CONTROL;
  return value || TAB_LISTA_CLIENTES;
}

export default function HonorariosDespacho() {
  const { user } = useAuth();
  const { permissions, role, isPlatformDesigner, loading: capabilitiesLoading } = useCapabilities();
  const readOnly = isDespachoReadOnly({ permissions, role, isPlatformDesigner, capabilitiesLoading, user });
  const matrixAllows = userCanAccessDespachoHonorarios(user);

  const [tab, setTab] = useState(TAB_LISTA_CLIENTES);
  const [caps, setCaps] = useState({});
  const [clients, setClients] = useState([]);
  const [boardRefreshKey, setBoardRefreshKey] = useState(0);
  const [syncDateFrom, setSyncDateFrom] = useState(DEFAULT_SYNC_DATE_FROM);
  const [syncDateTo, setSyncDateTo] = useState("");
  const [loadState, setLoadState] = useState(matrixAllows ? "loading" : "forbidden");
  const [syncBusy, setSyncBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [taskKinds, setTaskKinds] = useState([]);
  /** Mes de trabajo del módulo: no se pierde al cambiar de pestaña. */
  const [workPeriod, setWorkPeriodState] = useState(() => readStoredHonorariosWorkPeriod());
  const workParts = useMemo(() => splitHonorariosPeriod(workPeriod), [workPeriod]);

  const setPeriodYear = useCallback((year) => {
    setWorkPeriodState((prev) => {
      const parts = splitHonorariosPeriod(prev);
      return writeStoredHonorariosWorkPeriod(joinHonorariosPeriod(year, parts.month));
    });
  }, []);
  const setPeriodMonth = useCallback((month) => {
    setWorkPeriodState((prev) => {
      const parts = splitHonorariosPeriod(prev);
      return writeStoredHonorariosWorkPeriod(joinHonorariosPeriod(parts.year, month));
    });
  }, []);

  const canViewAmounts = caps.can_view_amounts === true;

  const loadAll = useCallback(async () => {
    if (!userCanAccessDespachoHonorarios(user)) {
      setLoadState("forbidden");
      return;
    }
    setLoadState("loading");
    try {
      const [capData, clientRows] = await Promise.all([
        honorariosApi.getCapabilities(),
        honorariosApi.listClients(),
      ]);
      if (capData?.can_access === false) {
        setCaps(capData);
        setLoadState("forbidden");
        return;
      }
      setCaps(capData);
      setClients(clientRows);
      setLoadState("ready");
    } catch (err) {
      const parsed = parseHonorariosError(err);
      setLoadState(parsed.kind === "forbidden" ? "forbidden" : "error");
      toast.error(parsed.message);
    }
  }, [user]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (loadState !== "ready") return;
    (async () => {
      try {
        const [kinds, tpls] = await Promise.all([
          honorariosApi.listHonorariosTaskKinds(),
          honorariosApi.listMessageTemplates(),
        ]);
        setTaskKinds(kinds);
        setTemplates(tpls);
      } catch {
        setTaskKinds([]);
        setTemplates([]);
      }
    })();
  }, [loadState]);

  const handleTabChange = (value) => {
    setTab(normalizeHonorariosTab(value));
  };

  const handleSyncInvoices = async (opts = {}) => {
    setSyncBusy(true);
    try {
      const issueFrom = opts.issue_date_from ?? syncDateFrom;
      const params = {
        ...(issueFrom ? { issue_date_from: issueFrom } : {}),
        ...(opts.period ? { period: opts.period } : {}),
      };
      const data = await honorariosApi.syncConfirm(params);
      const s = data.summary || {};
      toast.success(
        `Sincronizado: ${s.created ?? 0} facturas nuevas, ${s.updated ?? 0} actualizadas, ${s.payments_updated ?? 0} cobros SAT (PUE/complementos). Tablero actualizado.`,
      );
      setBoardRefreshKey((k) => k + 1);
      await loadAll();
      return data;
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
      return null;
    } finally {
      setSyncBusy(false);
    }
  };

  return (
    <DespachoPageShell
      title="Honorarios / Cobranza"
      subtitle="Control operativo de cobranza del mes. El cobro en Control confirma también el CFDI vinculado."
      testId="despacho-honorarios"
    >
      {readOnly ? (
        <Badge variant="secondary" className="rounded-sm mb-2">
          {DESPACHO_READ_ONLY_BADGE}
        </Badge>
      ) : null}

      {loadState === "loading" ? (
        <div className="flex items-center gap-2 text-sm text-slate-600 py-6">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Cargando honorarios…
        </div>
      ) : null}

      {loadState === "forbidden" ? (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
          data-testid="honorarios-access-denied"
        >
          Sin permiso para Control de honorarios. Pida al titular que active esa capacidad en{" "}
          <strong className="font-semibold">Equipo → Permisos</strong> (fila «Gestión Despacho →
          Control de honorarios y Contratos»).
        </div>
      ) : null}

      {loadState === "ready" ? (
        <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="!flex h-auto w-full max-w-6xl flex-wrap items-stretch justify-start gap-1 mb-4">
            <TabsTrigger
              value={TAB_LISTA_CLIENTES}
              className="text-xs rounded-sm flex-1 min-w-[7.5rem]"
              data-testid="tab-honorarios-lista-clientes"
            >
              Lista de clientes
            </TabsTrigger>
            <TabsTrigger
              value={TAB_CONTROL}
              className="text-xs rounded-sm flex-1 min-w-[7.5rem]"
              data-testid="tab-honorarios-control"
            >
              Control de cobranza
            </TabsTrigger>
            <TabsTrigger
              value={TAB_OTROS_COBROS}
              className="text-xs rounded-sm flex-1 min-w-[7.5rem]"
              data-testid="tab-honorarios-otros-cobros"
            >
              Otros cobros
            </TabsTrigger>
            <TabsTrigger
              value={TAB_OTROS_CARGOS}
              className="text-xs rounded-sm flex-1 min-w-[7.5rem]"
              data-testid="tab-honorarios-otros-cargos"
            >
              Notas del mes
            </TabsTrigger>
            <TabsTrigger
              value={TAB_CONTRATO}
              className="text-xs rounded-sm flex-1 min-w-[7.5rem]"
              data-testid="tab-honorarios-contrato-servicios"
            >
              Contrato de servicios
            </TabsTrigger>
            <TabsTrigger value={TAB_HISTORIAL} className="text-xs rounded-sm flex-1 min-w-[7.5rem]">
              Resumen General/Ingresos
            </TabsTrigger>
          </TabsList>

          <TabsContent value={TAB_LISTA_CLIENTES}>
            <HonorariosClientRosterPanel
              clients={clients}
              readOnly={readOnly}
              canViewAmounts={canViewAmounts}
              canExport={caps.can_export === true}
            />
          </TabsContent>

          <TabsContent value={TAB_CONTROL}>
            <HonorariosMonthlyBoard
              key={`control-${boardRefreshKey}`}
              readOnly={readOnly}
              canViewAmounts={canViewAmounts}
              canSync={caps.can_sync}
              canExport={caps.can_export}
              canGenerateMessages={caps.can_generate_messages}
              canConfirmPayment={caps.can_confirm_payment === true}
              canCreateHonorariosTasks={caps.can_create_honorarios_tasks === true}
              messageTemplates={templates}
              taskKinds={taskKinds}
              syncDateFrom={syncDateFrom}
              onSyncDateFromChange={setSyncDateFrom}
              syncDateTo={syncDateTo}
              onSyncDateToChange={setSyncDateTo}
              syncBusy={syncBusy}
              onSyncInvoices={handleSyncInvoices}
              viewMode="cobranza"
              defaultChip="pending"
              periodYear={workParts.year}
              periodMonth={workParts.month}
              onPeriodYearChange={setPeriodYear}
              onPeriodMonthChange={setPeriodMonth}
            />
            {(caps.emitter_rfcs || []).length ? (
              <p className="text-xs text-slate-500 mt-3">
                RFC emisor configurado: {(caps.emitter_rfcs || []).join(", ")}
              </p>
            ) : (
              <p className="text-xs text-amber-700 mt-3">
                Configure el RFC del despacho en licencia/perfil o en parámetros honorarios_emitter_rfcs.
              </p>
            )}
          </TabsContent>

          <TabsContent value={TAB_OTROS_COBROS}>
            <HonorariosOtrosCobrosPanel
              clients={clients}
              readOnly={readOnly}
              canViewAmounts={canViewAmounts}
            />
          </TabsContent>

          <TabsContent value={TAB_OTROS_CARGOS}>
            <HonorariosOtrosCargosPanel
              readOnly={readOnly}
              canViewAmounts={canViewAmounts}
              periodFilter={workPeriod}
            />
          </TabsContent>

          <TabsContent value={TAB_CONTRATO}>
            <HonorariosContratoServiciosPanel
              readOnly={readOnly}
              canViewAmounts={canViewAmounts}
            />
          </TabsContent>

          <TabsContent value={TAB_HISTORIAL}>
            <HonorariosHistorialPanel
              canViewAmounts={canViewAmounts}
              canExport={caps.can_export === true}
            />
          </TabsContent>
        </Tabs>
      ) : null}
    </DespachoPageShell>
  );
}
